/**
 * 交付物上传看板：底软交付物 / Momenta释放产物包两个 sheet，按项目、日期、来源统计。
 *
 * 成功/失败口径与「交付物上传列表」一致（后端 `display_upload_status`，含第三方上传覆盖），
 * 每条上传记录计一次。
 */

import { useEffect, useRef, useState } from 'react';
import {
  Button, Card, Col, Progress, Row, Spin, Tabs, Tooltip, Typography,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloudUploadOutlined,
  LoadingOutlined,
  ReloadOutlined,
  TrophyFilled,
  WarningFilled,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type {
  PeriodStats,
  UploadRecordSource,
  UploadStatsResponse,
} from '@/api/uploadRecords';
import { getUploadStats } from '@/api/uploadRecords';
import { useConfigStore } from '@/store/configStore';
import type { DeliverableType } from '@/constants/upload';
import UploadStatsFilters from './uploadStats/UploadStatsFilters';
import UploadStatsCharts from './uploadStats/UploadStatsCharts';
import UploadStatsTables from './uploadStats/UploadStatsTables';
import {
  PERIOD_LABELS,
  deriveGranularity,
  periodToRange,
  type Period,
} from './uploadStats/uploadStatsConfig';
import { useT } from '@/i18n';

const { Text, Title } = Typography;

const EMPTY_STATS: PeriodStats = {
  total: 0, done: 0, failed: 0, uploading: 0, success_rate: 0, fail_rate: 0,
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  circlePercent?: number;
  circleColor?: string;
}

const StatCard = ({ icon, label, value, color, subtitle, circlePercent, circleColor }: StatCardProps) => (
  <Card
    style={{ borderLeft: `4px solid ${color}`, borderRadius: 8, height: '100%' }}
    styles={{ body: { padding: '16px 20px' } }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ flex: 1 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
        {circlePercent !== undefined ? (
          <div style={{ marginTop: 8 }}>
            <Progress
              type="circle"
              percent={circlePercent}
              size={72}
              strokeColor={circleColor || color}
              format={(percent) => (
                <span style={{ fontSize: 14, fontWeight: 600, color }}>{percent}%</span>
              )}
            />
          </div>
        ) : (
          <div style={{ fontSize: 32, fontWeight: 700, color, marginTop: 4, lineHeight: 1.2 }}>
            {value}
          </div>
        )}
        {subtitle && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
            {subtitle}
          </Text>
        )}
      </div>
      <div style={{ fontSize: 36, color, opacity: 0.18, marginLeft: 8 }}>{icon}</div>
    </div>
  </Card>
);

const SummaryCards = ({ stats }: { stats: PeriodStats }) => {
  const { t } = useT();
  const { total, done, failed, uploading, success_rate, fail_rate } = stats;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} md={8} lg={5}>
        <StatCard
          icon={<CloudUploadOutlined />}
          label={t('上传次数')}
          value={total}
          color="#1677ff"
          subtitle={t('上传中: {count}', { count: uploading })}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={5}>
        <StatCard icon={<CheckCircleFilled />} label={t('上传成功')} value={done} color="#52c41a" />
      </Col>
      <Col xs={24} sm={12} md={8} lg={5}>
        <StatCard icon={<CloseCircleFilled />} label={t('上传失败')} value={failed} color="#ff4d4f" />
      </Col>
      <Col xs={24} sm={12} md={8} lg={5}>
        <StatCard
          icon={<TrophyFilled />}
          label={t('成功率')}
          value={`${success_rate}%`}
          color="#52c41a"
          circlePercent={success_rate}
          circleColor="#52c41a"
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={4}>
        <StatCard
          icon={<WarningFilled />}
          label={t('失败率')}
          value={`${fail_rate}%`}
          color={fail_rate > 0 ? '#ff4d4f' : '#8c8c8c'}
          circlePercent={fail_rate}
          circleColor={fail_rate > 0 ? '#ff4d4f' : '#d9d9d9'}
        />
      </Col>
    </Row>
  );
};

const UploadStatsDashboard = () => {
  const { t } = useT();
  const { projects, fetchProjects } = useConfigStore();
  const [board, setBoard] = useState<DeliverableType>('package');
  const [period, setPeriod] = useState<Period>('today');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [source, setSource] = useState<UploadRecordSource | undefined>();
  const [data, setData] = useState<UploadStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const seqRef = useRef(0);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const days = periodToRange(period, range);
    if (!days) return;
    const seq = ++seqRef.current;
    setLoading(true);
    getUploadStats({
      deliverable_type: board,
      project_id: projectId,
      source,
      date_from: days[0].format('YYYY-MM-DD'),
      date_to: days[1].format('YYYY-MM-DD'),
      granularity: deriveGranularity(days[0], days[1]),
    })
      .then((res) => {
        if (seq === seqRef.current) setData(res);
      })
      .catch(() => undefined)
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
  }, [board, period, range, projectId, source, refreshTick]);

  // ECharts 在 Tabs/异步数据下可能量到 0 宽，等两帧再挂
  useEffect(() => {
    if (!data || data.summary.total === 0) {
      setChartReady(false);
      return;
    }
    setChartReady(false);
    let rafId1 = 0;
    let rafId2 = 0;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => setChartReady(true));
    });
    return () => {
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
    };
  }, [data]);

  const handleBoardChange = (key: string) => {
    setBoard(key as DeliverableType);
    setSource(undefined);
  };

  const handlePeriodChange = (next: Period) => {
    if (next === 'custom' && !range) {
      setRange([dayjs().subtract(29, 'day').startOf('day'), dayjs().startOf('day')]);
    }
    setPeriod(next);
  };

  return (
    <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24 }} />}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={5} style={{ margin: 0 }}>{t('交付物上传看板')}</Title>
        <Tooltip title={t('刷新数据')}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setRefreshTick((tick) => tick + 1)}
            loading={loading}
          >
            {t('刷新')}
          </Button>
        </Tooltip>
      </div>

      <Tabs
        activeKey={board}
        onChange={handleBoardChange}
        items={[
          { key: 'package', label: t('底软交付物') },
          { key: 'external_release', label: t('Momenta释放产物包') },
        ]}
        style={{ marginBottom: 4 }}
      />

      <div style={{ marginBottom: 16 }}>
        <UploadStatsFilters
          board={board}
          period={period}
          onPeriodChange={handlePeriodChange}
          range={range}
          onRangeChange={setRange}
          projectId={projectId}
          onProjectChange={setProjectId}
          source={source}
          onSourceChange={setSource}
          projects={projects.map((project) => ({ id: project.id, name: project.name }))}
        />
      </div>

      <Card
        style={{ marginBottom: 16, borderRadius: 8 }}
        styles={{ body: { paddingBottom: 16 } }}
        title={
          <Text strong style={{ fontSize: 14 }}>
            {t('{period}汇总', { period: t(PERIOD_LABELS[period]) })}
            {data?.range && `（${data.range.date_from} ~ ${data.range.date_to}）`}
          </Text>
        }
      >
        <SummaryCards stats={data?.summary ?? EMPTY_STATS} />
      </Card>

      <UploadStatsCharts data={data} chartReady={chartReady} />

      <UploadStatsTables
        bySource={data?.by_source ?? []}
        byProject={data?.by_project ?? []}
      />
    </Spin>
  );
};

export default UploadStatsDashboard;
